/**
 * Gvia (Payment Collection) composable
 * Manages state and logic for agent payment collection flow
 */
import { ref, computed, readonly } from 'vue'
import { httpsCallable } from 'firebase/functions'
import { getFirebaseFunctions } from '@/core/firebase/firebase'
import {
  addWalletTransaction,
  walletExists,
  createWallet,
  getNextPaymentApprovalNum,
} from '@/core/services/wallet'
import { uploadFile } from '@/core/firebase/storage'
import { getEmailRecipients } from '@/core/services/emailSections'
import { useEmail } from '@/core/composables/useEmail'
import { usePdf } from '@/core/composables/usePdf'
import {
  generateCashPaymentEmailHtml,
  generateCashPaymentPdfHtml,
  TEST_RECEIPT_NOTICE,
} from '@/core/utils/emailTemplates'

// Shared state across components
const clientData = ref(null)
const selectedPayments = ref([])
const contactSelected = ref([])
const currentStep = ref('select') // 'select' | 'payments' | 'success'

/**
 * Composable for payment collection (גבייה)
 * @returns {Object} Gvia methods and state
 */
export function useGvia() {
  const loading = ref(false)
  const error = ref(null)

  // ============================================
  // Computed Properties
  // ============================================

  /**
   * Calculate total payment amount from selected invoices
   */
  const paymentAmount = computed(() => {
    if (selectedPayments.value.length === 0) return 0

    const sum = selectedPayments.value.reduce((total, payment) => {
      return total + (payment.SUMDEBIT - payment.SUMCREDIT)
    }, 0)

    return Number(sum.toFixed(2))
  })

  /**
   * Calculate remaining balance (total - selected)
   */
  const remainingBalance = computed(() => {
    if (!clientData.value?.paymentsData) return 0

    const totalDebt = clientData.value.paymentsData.reduce((total, payment) => {
      return total + (payment.SUMDEBIT - payment.SUMCREDIT)
    }, 0)

    return Number((totalDebt - paymentAmount.value).toFixed(2))
  })

  /**
   * Check if client has any invoices
   */
  const hasInvoices = computed(() => {
    return clientData.value?.paymentsData?.length > 0
  })

  /**
   * Check if all payments are selected
   */
  const allSelected = computed(() => {
    if (!clientData.value?.paymentsData?.length) return false
    return selectedPayments.value.length === clientData.value.paymentsData.length
  })

  // ============================================
  // Cloud Function Calls
  // ============================================

  /**
   * Fetch client payments data from Priority ERP
   * @param {string} clientNum - Client number
   * @returns {Promise<Object>} Client data with invoices
   */
  async function fetchClientPayments(clientNum) {
    loading.value = true
    error.value = null

    try {
      const functions = getFirebaseFunctions()
      const callable = httpsCallable(functions, 'paymentsGetClientPayments')
      const result = await callable({ clientNum })

      // Store client data
      clientData.value = result.data

      // Reset selections
      selectedPayments.value = []
      contactSelected.value = []

      // Mark all payments as unchecked
      if (clientData.value?.paymentsData) {
        clientData.value.paymentsData = clientData.value.paymentsData.map((p) => ({
          ...p,
          checked: false,
        }))
      }

      return result.data
    } catch (err) {
      console.error('fetchClientPayments error:', err)
      error.value = err.message || 'שגיאה בטעינת נתוני לקוח'
      throw err
    } finally {
      loading.value = false
    }
  }

  /**
   * Generate payment link via PayPlus
   * @param {number} amount - Payment amount (optional, uses selected if not provided)
   * @param {boolean} isManual - Whether this is a manual amount entry
   * @param {string} [note] - Optional agent note (stored in AgentSlika, shown in confirmation email)
   * @returns {Promise<string>} Payment page URL
   */
  async function generatePaymentLink(amount = null, isManual = false, note = '') {
    if (!clientData.value) {
      throw new Error('No client selected')
    }

    loading.value = true
    error.value = null

    try {
      const functions = getFirebaseFunctions()
      const callable = httpsCallable(functions, 'paymentsGeneratePaymentLink')

      // Prepare payment data
      const paymentData = {
        clientNum: clientData.value.clientNum,
        clientName: clientData.value.clientName,
        clientVat: clientData.value.clientVat,
        contactSelected: contactSelected.value,
        amount: amount || paymentAmount.value,
        isManualAmount: isManual,
        itra: remainingBalance.value,
        note: note || '',
      }

      // Include selected payments info if not manual
      if (!isManual && selectedPayments.value.length > 0) {
        paymentData.selectedPayments = selectedPayments.value.map((p) => ({
          IVNUM: p.IVNUM,
          BALDATE: p.BALDATE,
          FNCDATE: p.FNCDATE,
          amount: p.SUMDEBIT - p.SUMCREDIT,
        }))
      }

      const result = await callable(paymentData)

      return result.data.paymentLink
    } catch (err) {
      console.error('generatePaymentLink error:', err)
      error.value = err.message || 'שגיאה ביצירת קישור תשלום'
      throw err
    } finally {
      loading.value = false
    }
  }

  // ============================================
  // Payment Selection Methods
  // ============================================

  /**
   * Toggle payment selection
   * @param {Object} payment - Payment object
   */
  function togglePaymentSelection(payment) {
    // Find the raw (non-readonly) payment object in clientData
    const rawPayment = clientData.value?.paymentsData?.find((p) => p.IVNUM === payment.IVNUM)
    if (!rawPayment) return

    rawPayment.checked = !rawPayment.checked

    if (rawPayment.checked) {
      selectedPayments.value.push(rawPayment)
    } else {
      const index = selectedPayments.value.findIndex((p) => p.IVNUM === payment.IVNUM)
      if (index !== -1) {
        selectedPayments.value.splice(index, 1)
      }
    }
  }

  /**
   * Select or deselect all payments
   * @param {boolean} selectAll - Whether to select all
   */
  function toggleSelectAll(selectAll = null) {
    const shouldSelectAll = selectAll !== null ? selectAll : !allSelected.value

    if (shouldSelectAll) {
      // Select all
      clientData.value.paymentsData.forEach((p) => (p.checked = true))
      selectedPayments.value = [...clientData.value.paymentsData]
    } else {
      // Deselect all
      clientData.value.paymentsData.forEach((p) => (p.checked = false))
      selectedPayments.value = []
    }
  }

  // ============================================
  // Navigation Methods
  // ============================================

  /**
   * Move to payments step
   */
  function goToPayments() {
    if (!clientData.value) {
      throw new Error('No client selected')
    }
    currentStep.value = 'payments'
  }

  /**
   * Go back to client selection
   */
  function goToSelect() {
    currentStep.value = 'select'
  }

  /**
   * Reset all state
   */
  function reset() {
    clientData.value = null
    selectedPayments.value = []
    contactSelected.value = []
    currentStep.value = 'select'
    error.value = null
  }

  /**
   * Clear client data but stay on select step
   */
  function clearClient() {
    clientData.value = null
    selectedPayments.value = []
    contactSelected.value = []
  }

  // ============================================
  // Unified Email Sending
  // ============================================

  /**
   * Send cash payment confirmation email with PDF attachment
   * Single source of truth — used by confirmCashPayment, confirmPayment, and resend
   * @param {Object} params
   * @param {Object} params.emailData - Data for generateCashPaymentEmailHtml / generateCashPaymentPdfHtml
   * @param {string[]} params.clientEmails - Client contact email addresses
   * @param {string|null} params.approvalNum - Payment approval number (optional)
   * @param {string} params.clientName - Client display name
   * @param {string} params.clientNum - Client number
   */
  async function sendCashPaymentConfirmationEmail({
    emailData,
    clientEmails = [],
    approvalNum = null,
    clientName = '',
    clientNum = '',
  }) {
    const staffEmails = await getEmailRecipients('cashPaymentConfirm')
    const allRecipients = [...new Set([...clientEmails, ...staffEmails])]

    if (allRecipients.length === 0) return

    const { sendEmail } = useEmail()
    const { generatePdfAsBase64 } = usePdf()

    const emailHtml = generateCashPaymentEmailHtml(emailData)
    const pdfHtml = generateCashPaymentPdfHtml(emailData)

    const pdfFilename = approvalNum ? `אישור-תשלום-${approvalNum}` : 'אישור-תשלום'

    const pdfBase64 = await generatePdfAsBase64(pdfHtml, pdfFilename)

    const approvalPart = approvalNum ? `#${approvalNum} - ` : ''
    const subject = `אישור תשלום ${approvalPart}${clientName} (${clientNum})`

    await sendEmail({
      to: allRecipients,
      subject,
      html: emailHtml,
      from: 'GoFood גבייה',
      attachments: [{ filename: `${pdfFilename}.pdf`, content: pdfBase64 }],
    })
  }

  /**
   * Admin only: send a sample cash receipt (email + PDF) to ONE contact email.
   * Real client/agent details, clearly fake amounts/invoices. Staff recipients are NOT included.
   * @param {string} email - Target contact email
   * @param {Object} [agentInfo] - { name, number }
   */
  async function sendTestReceiptEmail(email, agentInfo = {}) {
    if (!clientData.value) throw new Error('No client selected')
    if (!email) throw new Error('No email selected')

    const { sendEmail } = useEmail()
    const { generatePdfAsBase64 } = usePdf()

    const emailData = {
      isTest: true,
      clientName: clientData.value.clientName,
      clientNum: clientData.value.clientNum,
      clientVat: clientData.value.clientVat || '',
      agentName: agentInfo.name || '',
      agentNumber: agentInfo.number || '',
      amount: 0,
      cashAmount: 0,
      paymentApprovalNum: '000000',
      invoices: [{ IVNUM: 'דוגמה', BALDATE: null, FNCDATE: null, amount: 0 }],
      note: TEST_RECEIPT_NOTICE,
    }

    const pdfBase64 = await generatePdfAsBase64(
      generateCashPaymentPdfHtml(emailData),
      'אישור-תשלום-ניסיון'
    )

    await sendEmail({
      to: [email],
      subject: `מייל ניסיון - אישור תשלום - ${clientData.value.clientName} (${clientData.value.clientNum})`,
      html: generateCashPaymentEmailHtml(emailData),
      from: 'GoFood גבייה',
      attachments: [{ filename: 'אישור-תשלום-ניסיון.pdf', content: pdfBase64 }],
    })
  }

  // ============================================
  // Cash Payment
  // ============================================

  /**
   * Confirm a cash payment - saves transaction to wallet and sends email
   * @param {string} uid - Agent's Firebase Auth UID
   * @param {string} [agentId] - Agent identifier
   * @param {Object} [agentInfo] - Agent display info { name, number }
   * @returns {Promise<string>} Transaction ID
   */
  async function confirmCashPayment(uid, agentId, agentInfo = {}) {
    if (!clientData.value) {
      throw new Error('No client selected')
    }
    if (paymentAmount.value <= 0) {
      throw new Error('No invoices selected')
    }

    loading.value = true
    error.value = null

    try {
      // Ensure wallet exists
      const exists = await walletExists(uid)
      if (!exists) {
        await createWallet(uid)
      }

      // Prepare invoice data for the transaction record
      const invoices = selectedPayments.value.map((p) => ({
        IVNUM: p.IVNUM,
        BALDATE: p.BALDATE,
        FNCDATE: p.FNCDATE,
        amount: p.SUMDEBIT - p.SUMCREDIT,
      }))

      // Save transaction to wallet
      const txId = await addWalletTransaction(uid, 'cash', {
        amount: paymentAmount.value,
        clientNum: clientData.value.clientNum,
        clientName: clientData.value.clientName,
        clientVat: clientData.value.clientVat || '',
        invoices,
        agentId: agentId || '',
        contactEmails: [...contactSelected.value],
      })

      // Send confirmation email with PDF (non-blocking)
      try {
        await sendCashPaymentConfirmationEmail({
          emailData: {
            clientName: clientData.value.clientName,
            clientNum: clientData.value.clientNum,
            clientVat: clientData.value.clientVat || '',
            amount: paymentAmount.value,
            agentName: agentInfo.name || '',
            agentNumber: agentInfo.number || agentId || '',
            invoices,
          },
          clientEmails: [...contactSelected.value],
          clientName: clientData.value.clientName,
          clientNum: clientData.value.clientNum,
        })
      } catch (emailErr) {
        console.error('Cash payment email failed (payment was saved):', emailErr)
      }

      return txId
    } catch (err) {
      console.error('confirmCashPayment error:', err)
      error.value = err.message || 'שגיאה בשמירת תשלום מזומן'
      throw err
    } finally {
      loading.value = false
    }
  }

  // ============================================
  // Unified Payment (Cash + Checks with Signature)
  // ============================================

  /**
   * Confirm a payment with signature — handles split cash/checks, approval number, and email
   * @param {string} uid - Agent's Firebase Auth UID
   * @param {Object} agentInfo - Agent display info { name, number }
   * @param {Object} allocation - Payment allocation from GviaPaymentMethodModal
   * @param {number} allocation.cash - Cash amount
   * @param {number} allocation.checks - Checks total amount
   * @param {Array} allocation.checksDetails - Array of { checkNumber, date, amount }
   * @param {boolean} allocation.isManual - Whether this is a manual amount (no invoices)
   * @param {number} allocation.amount - Total effective amount
   * @param {string} [allocation.note] - Optional agent note (saved on transactions, shown in email + PDF)
   * @param {Blob} signatureBlob - Signature canvas blob
   * @returns {Promise<{ approvalNum: string, txIds: string[] }>}
   */
  async function confirmPayment(uid, agentInfo, allocation, signatureBlob) {
    if (!clientData.value) {
      throw new Error('No client selected')
    }
    if (allocation.amount <= 0) {
      throw new Error('Payment amount must be greater than 0')
    }

    loading.value = true
    error.value = null

    try {
      // 1. Get payment approval number (atomic)
      const approvalNum = await getNextPaymentApprovalNum()

      // 2. Upload signature to Firebase Storage
      let signatureUrl = ''
      if (signatureBlob) {
        signatureUrl = await uploadFile(`signatures/gvia/${approvalNum}.png`, signatureBlob)
      }

      // 3. Ensure wallet exists
      const exists = await walletExists(uid)
      if (!exists) {
        await createWallet(uid)
      }

      // 4. Prepare shared transaction data
      const invoices = allocation.isManual
        ? []
        : selectedPayments.value.map((p) => ({
            IVNUM: p.IVNUM,
            BALDATE: p.BALDATE,
            FNCDATE: p.FNCDATE,
            amount: p.SUMDEBIT - p.SUMCREDIT,
          }))

      const sharedData = {
        clientNum: clientData.value.clientNum,
        clientName: clientData.value.clientName,
        clientVat: clientData.value.clientVat || '',
        invoices,
        agentId: agentInfo.number || '',
        contactEmails: [...contactSelected.value],
        paymentApprovalNum: approvalNum,
        signatureUrl,
        isManual: allocation.isManual,
        note: allocation.note || '',
      }

      // 5. Save to wallet buckets
      const txIds = []

      if (allocation.cash > 0) {
        const cashTxId = await addWalletTransaction(uid, 'cash', {
          ...sharedData,
          amount: allocation.cash,
        })
        txIds.push(cashTxId)
      }

      if (allocation.checks > 0) {
        const checksTxId = await addWalletTransaction(uid, 'checks', {
          ...sharedData,
          amount: allocation.checks,
          checksDetails: allocation.checksDetails.map((c) => ({
            checkNumber: c.checkNumber,
            date: c.date,
            amount: parseFloat(c.amount) || 0,
          })),
        })
        txIds.push(checksTxId)
      }

      // 6. Send confirmation email with PDF (non-blocking)
      try {
        await sendCashPaymentConfirmationEmail({
          emailData: {
            clientName: clientData.value.clientName,
            clientNum: clientData.value.clientNum,
            clientVat: clientData.value.clientVat || '',
            amount: allocation.amount,
            cashAmount: allocation.cash,
            checksAmount: allocation.checks,
            checksDetails: allocation.checksDetails || [],
            agentName: agentInfo.name || '',
            agentNumber: agentInfo.number || '',
            invoices,
            isManual: allocation.isManual,
            paymentApprovalNum: approvalNum,
            signatureUrl,
            note: allocation.note || '',
          },
          clientEmails: [...contactSelected.value],
          approvalNum,
          clientName: clientData.value.clientName,
          clientNum: clientData.value.clientNum,
        })
      } catch (emailErr) {
        console.error('Payment email failed (payment was saved):', emailErr)
      }

      return { approvalNum, txIds }
    } catch (err) {
      console.error('confirmPayment error:', err)
      error.value = err.message || 'שגיאה בשמירת תשלום'
      throw err
    } finally {
      loading.value = false
    }
  }

  // ============================================
  // Utility Methods
  // ============================================

  /**
   * Format date string to DD/MM/YYYY
   * @param {string} dateStr - Date string
   * @returns {string} Formatted date
   */
  function formatDate(dateStr) {
    if (!dateStr) return ''
    const date = new Date(dateStr)
    const day = String(date.getDate()).padStart(2, '0')
    const month = String(date.getMonth() + 1).padStart(2, '0')
    const year = date.getFullYear()
    return `${day}/${month}/${year}`
  }

  /**
   * Format currency amount
   * @param {number} amount - Amount to format
   * @returns {string} Formatted amount with currency symbol
   */
  function formatCurrency(amount) {
    return new Intl.NumberFormat('he-IL', {
      style: 'currency',
      currency: 'ILS',
    }).format(amount)
  }

  return {
    // State (readonly to prevent external mutations)
    clientData: readonly(clientData),
    selectedPayments: readonly(selectedPayments),
    contactSelected,
    currentStep: readonly(currentStep),
    loading,
    error,

    // Computed
    paymentAmount,
    remainingBalance,
    hasInvoices,
    allSelected,

    // Cloud functions
    fetchClientPayments,
    generatePaymentLink,
    confirmCashPayment,
    confirmPayment,
    sendCashPaymentConfirmationEmail,
    sendTestReceiptEmail,

    // Selection
    togglePaymentSelection,
    toggleSelectAll,

    // Navigation
    goToPayments,
    goToSelect,
    reset,
    clearClient,

    // Utilities
    formatDate,
    formatCurrency,
  }
}
